import React, { useState, useEffect } from 'react';
import { getFridges, getProducts, assignProduct, getFridgeProducts } from '../../services/api';
import toast from 'react-hot-toast';

const AssignProducts = () => {
  const [fridges, setFridges] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedFridge, setSelectedFridge] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [minStockLevel, setMinStockLevel] = useState(5);
  const [fridgeProducts, setFridgeProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedFridge) {
      loadFridgeProducts(selectedFridge);
    }
  }, [selectedFridge]);

  const loadData = async () => {
    try {
      const [fridgesRes, productsRes] = await Promise.all([
        getFridges(),
        getProducts()
      ]);
      
      setFridges(fridgesRes.data.fridges || []);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadFridgeProducts = async (fridgeId) => {
    try {
      const response = await getFridgeProducts(fridgeId);
      setFridgeProducts(response.data.products || []);
    } catch (error) {
      console.error('Error loading fridge products:', error);
      toast.error('Failed to load fridge inventory');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFridge || !selectedProduct || !quantity) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const data = {
        fridge_id: parseInt(selectedFridge),
        product_id: parseInt(selectedProduct),
        quantity: parseInt(quantity),
        expiry_date: expiryDate || null,
        min_stock_level: parseInt(minStockLevel)
      };
      
      const response = await assignProduct(data);
      
      if (response.data.success) {
        toast.success('Product assigned successfully');
        
        // Reset form
        setSelectedProduct('');
        setQuantity('');
        setExpiryDate('');
        setMinStockLevel(5);
        
        // Reload fridge products
        loadFridgeProducts(selectedFridge);
      } else {
        toast.error(response.data.message || 'Failed to assign product');
      }
    } catch (error) {
      console.error('Error assigning product:', error);
      toast.error(error.response?.data?.message || 'Failed to assign product');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Assign Products to Fridge</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Assignment Form */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Assign New Stock</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Select Fridge *
                </label>
                <select
                  value={selectedFridge}
                  onChange={(e) => setSelectedFridge(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  required
                >
                  <option value="">Choose a fridge</option>
                  {fridges.map((fridge) => (
                    <option key={fridge.id} value={fridge.id}>
                      {fridge.fridge_id} - {fridge.apartment_name} ({fridge.location || 'No location'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Select Product *
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  required
                >
                  <option value="">Choose a product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ₹{product.price}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Quantity *
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  min="1"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional - leave empty to use product's default expiry
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Minimum Stock Alert Level
                </label>
                <input
                  type="number"
                  value={minStockLevel}
                  onChange={(e) => setMinStockLevel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  min="1"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className={`w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition duration-200 ${
                  submitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {submitting ? 'Assigning...' : 'Assign Product'}
              </button>
            </form>
          </div>

          {/* Current Stock Status */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Current Stock Status</h2>
            
            {selectedFridge ? (
              <div>
                <p className="text-gray-600 mb-4">
                  Showing stock for: {fridges.find(f => f.id === parseInt(selectedFridge))?.apartment_name}
                </p>
                
                {fridgeProducts.length === 0 ? (
                  <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-lg">
                    <p>No products assigned to this fridge yet</p>
                    <p className="text-sm mt-2">Use the form to assign products</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {fridgeProducts.map((fp) => (
                      <div key={fp.id} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-semibold">{fp.name}</h4>
                            <p className="text-sm text-gray-600">
                              Price: ₹{fp.price}
                            </p>
                            <p className="text-sm text-gray-600">
                              Current Stock: {fp.quantity} units
                            </p>
                            <p className="text-sm text-gray-600">
                              Min Alert: {fp.min_stock_level}
                            </p>
                            {fp.expiry_date && (
                              <p className="text-sm text-gray-600">
                                Expires: {new Date(fp.expiry_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            fp.quantity > fp.min_stock_level 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {fp.quantity > fp.min_stock_level ? 'In Stock' : 'Low Stock'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12 bg-gray-50 rounded-lg">
                <p className="text-lg">Select a fridge to view current stock</p>
                <p className="text-sm mt-2">Choose from the dropdown to see inventory</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignProducts;